import ticketsApi from 'api/requests/tickets';
import imageUploadApi from 'api/requests/imageUpload';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import NewProductForm from './NewProductForm';
import ProductSuggestions from './ProductSuggestions';
import ProductCatalogPicker from './ProductCatalogPicker';
import StepSection from './StepSection';
import Loader from 'components/_UI/Loader';
import breakpoints from 'theme/breakpoints';
import { useEvent } from 'context/events/Event';
import { SaleTypes } from 'modules/_common/SaleTypes';
import { ArrowLeft, ArrowRight, Check, PackageCheck, PenLine, Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function blankNewProduct() {
    return {
        internal_id: uuidv4(),
        name: '',
        description: '',
        quantity: '',
        price: '',
        floatPrice: 0,
        type: 'product',
        imageFile: null,
        showErrors: false,
    };
}

// Sugestão da IA vira um rascunho comum: mesmos campos, já preenchidos e ainda
// editáveis. Só `type` vem do modelo (product | sales_promotion | voucher) —
// nunca 'ticket', que tem outro fluxo.
function newProductFromSuggestion(suggestion) {
    const price = Number(suggestion?.suggested_price) || 0;

    return {
        ...blankNewProduct(),
        name: suggestion?.name || '',
        description: suggestion?.description || '',
        quantity: String(suggestion?.suggested_quantity ?? ''),
        // O input de moeda é pt-BR: precisa da vírgula pra exibir os centavos.
        price: price.toFixed(2).replace('.', ','),
        floatPrice: price,
        type: suggestion?.type || 'product',
        fromSuggestion: true,
    };
}

// Produto que já está no evento, aberto pra edição nesta etapa. Guarda `id` e o
// id do lote porque o /products/bulk precisa dos dois pra atualizar em vez de
// criar de novo.
function eventProductToDraft(product) {
    const batch = product?.batches?.[0];
    const price = Number(product?.price ?? batch?.price) || 0;

    return {
        internal_id: `existing-${product?.id}`,
        id: product?.id,
        batch_id: batch?.id,
        batch_is_active: batch?.is_active,
        name: product?.name || '',
        description: product?.description || '',
        quantity: String(product?.quantity ?? batch?.quantity ?? ''),
        price: price.toFixed(2).replace('.', ','),
        floatPrice: price,
        type: product?.type || 'product',
        image: product?.image || null,
        image_id: product?.image_id || null,
        // Guardado à parte: se o produtor trocar ou apagar a foto, é este id que
        // precisa ser removido do storage depois de salvar.
        original_image_id: product?.image_id || null,
        imageFile: null,
        showErrors: false,
    };
}

function isProductValid(product) {
    return Boolean(product.name?.trim()) && Number(product.quantity) > 0 && product.floatPrice > 0;
}

// Modelo do catálogo ("Geral") virando rascunho: mesma ideia de
// newProductFromSuggestion, mas products_template não tem `type` (é sempre
// produto simples) e já carrega uma foto real — diferente da IA, que nunca
// tem imagem.
function newProductFromTemplate(template) {
    const price = Number(template?.price) || 0;

    return {
        ...blankNewProduct(),
        name: template?.name || '',
        description: template?.description || '',
        quantity: String(template?.quantity ?? ''),
        price: price.toFixed(2).replace('.', ','),
        floatPrice: price,
        type: 'product',
        image: template?.image || null,
        image_id: template?.image_id || null,
        product_template_id: template?.id,
        fromTemplate: true,
    };
}

export default function ProductsToEvent({
    edit,
    isDraft,
    availableProducts,
    productsPagination,
    productsLoading,
    onProductPaginate = () => false,
    highlightProductId,
    onSuccess = () => false,
    goBackFormStep = () => false,
}) {
    const [loading, setLoading] = useState(false);
    const [existingProducts, setExistingProducts] = useState([]);
    const [newProducts, setNewProducts] = useState([]);
    const [pendingScrollId, setPendingScrollId] = useState(null);
    // Só um produto aberto por vez, como nos ingressos: com a lista inteira
    // expandida não dá pra ver o que o evento vende sem rolar tudo.
    const [expandedId, setExpandedId] = useState(null);

    const { event } = useEvent();

    // Salvar exige tudo válido; adicionar mais um rascunho só depende dos
    // rascunhos — um produto existente incompleto não é motivo pra travar o
    // botão de criar outro.
    const hasNewDraftErrors = newProducts.some(product => !isProductValid(product));
    const hasProductErrors = hasNewDraftErrors
        || existingProducts.some(product => !isProductValid(product));

    // Nomes já em jogo nesta etapa. Some com a sugestão equivalente pra não
    // oferecer duas vezes a mesma coisa (o backend só conhece os que já estão
    // salvos, não os rascunhos abertos aqui).
    const takenProductNames = [
        ...existingProducts.map(product => product?.name),
        ...newProducts.map(product => product?.name),
    ];

    // Por id, não por nome: sobrevive a renomear o rascunho depois de
    // escolhido, diferente do dedup por nome acima.
    const pickedTemplateIds = newProducts
        .filter(product => product?.product_template_id)
        .map(product => product.product_template_id);

    // Quanto o evento vai vender depois de salvar. Cada parcela tem uma seção
    // visível na tela — o número nunca pode ser maior que o que dá pra ver.
    const totalChosen = existingProducts.length + newProducts.length;

    function toggleExpanded(internalId) {
        setExpandedId(prev => (prev === internalId ? null : internalId));
    }

    function addNewProduct() {
        if (hasNewDraftErrors) return;

        const draft = blankNewProduct();

        setNewProducts(prev => [...prev, draft]);
        // Rascunho novo nasce vazio: fechado ele seria só um cabeçalho sem nada.
        setExpandedId(draft.internal_id);
    }

    function updateNewProduct(internalId, patch) {
        setNewProducts(prev => prev.map(p => (p.internal_id === internalId ? { ...p, ...patch } : p)));
    }

    function removeNewProduct(internalId) {
        setNewProducts(prev => prev.filter(p => p.internal_id !== internalId));
        setExpandedId(prev => (prev === internalId ? null : prev));
    }

    function acceptSuggestion(suggestion) {
        const draft = newProductFromSuggestion(suggestion);

        setNewProducts(prev => [...prev, draft]);
        setExpandedId(draft.internal_id);
        setPendingScrollId(draft.internal_id);
    }

    function acceptTemplate(template) {
        const draft = newProductFromTemplate(template);

        setNewProducts(prev => [...prev, draft]);
        setExpandedId(draft.internal_id);
        setPendingScrollId(draft.internal_id);
    }

    // "Criar do zero" fica abaixo do catálogo e das sugestões, então o
    // rascunho recém-aceito nasce fora da tela: sem levar o produtor até ele,
    // o clique em "Adicionar" parece não ter feito nada.
    useEffect(() => {
        if (!pendingScrollId) return;

        document
            .getElementById(`product-draft-${pendingScrollId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        setPendingScrollId(null);
    }, [pendingScrollId]);

    // Os produtos do evento viram rascunhos editáveis. Roda a cada troca de
    // evento (inclusive no refetch ao voltar de etapa), então o que o produtor
    // digitou e ainda não salvou é substituído pelo que está no servidor.
    useEffect(() => {
        const products = (event?.products || []).filter(product => product?.type !== 'ticket');
        const drafts = products.map(eventProductToDraft);

        setExistingProducts(drafts);

        // Veio de "editar" na aba "A venda em eventos": abre e rola direto
        // pro produto que o produtor clicou, em vez do primeiro da lista.
        const target = highlightProductId && drafts.find(d => String(d.id) === String(highlightProductId));

        // Ao carregar, só o primeiro produto fica aberto — os outros ficam como
        // resumo, pra lista inteira caber na tela.
        setExpandedId(prev => prev ?? target?.internal_id ?? drafts[0]?.internal_id ?? null);

        if (target) {
            setPendingScrollId(target.internal_id);
        }
    }, [event]);

    function updateExistingProduct(internalId, patch) {
        setExistingProducts(prev => prev.map(p => (p.internal_id === internalId ? { ...p, ...patch } : p)));
    }

    // Produtos compartilham com os ingressos a config de taxa absorvida do
    // evento (tudo ou nada).
    function getAbsorbedServiceFee() {
        return (event?.products || [])
            .find(item => item?.type === 'ticket')?.absorbed_service_fee ?? false;
    }

    /*
     * O /products/bulk com `type: 'products'` desanexa do evento todo produto do
     * tipo `product` antes de reprocessar o payload — o que não for enviado some
     * do evento. Por isso os existentes vão junto em toda gravação, com `id` e
     * `batches[].id` pra serem atualizados no lugar de recriados.
     */
    async function buildExistingProductPayloads() {
        const absorbedServiceFee = getAbsorbedServiceFee();

        const payloads = [];

        for (const item of existingProducts) {
            // Foto trocada nesta etapa: sobe agora e o produto passa a apontar
            // pro arquivo novo.
            const uploadedImageId = item.imageFile
                ? await uploadProductImage(item.imageFile)
                : null;

            const payload = {
                id: item.id,
                event_id: event?.id,
                name: item.name,
                price: item.floatPrice,
                quantity: Number(item.quantity),
                type: item.type || 'product',
                sale_type: SaleTypes.BATCH,
                absorbed_service_fee: absorbedServiceFee,
            };

            if (item.description) {
                payload.description = item.description;
            }

            const imageId = uploadedImageId || item.image_id;

            if (imageId) {
                payload.image_id = imageId;
            }

            if (item.batch_id) {
                payload.batches = [{
                    id: item.batch_id,
                    is_active: item.batch_is_active,
                    name: item.name,
                    price: item.floatPrice,
                    quantity: Number(item.quantity),
                }];

                if (item.description) {
                    payload.batches[0].description = item.description;
                }
            }

            payloads.push(payload);
        }

        return payloads;
    }

    async function uploadProductImage(file) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'products');

        const { data } = await imageUploadApi.uploadWithFolder(formData);

        return data?.id;
    }

    // A imagem antiga só sai do storage depois que o produto já aponta pra
    // outra (ou pra nenhuma) — falhar aqui não pode derrubar o salvamento.
    async function removeReplacedImages() {
        const orphanIds = existingProducts
            .filter(item => item.original_image_id && item.original_image_id !== item.image_id)
            .map(item => item.original_image_id);

        for (const imageId of orphanIds) {
            try {
                await imageUploadApi.delete(imageId);
            } catch (error) {
                // console.log('error')
            }
        }
    }

    // Produtos digitados direto nesta etapa — entram no mesmo array enviado a
    // /products/bulk, numa única chamada junto com os que já existiam.
    async function buildNewProductPayloads() {
        const absorbedServiceFee = getAbsorbedServiceFee();

        const payloads = [];

        for (const draft of newProducts) {
            const payload = {
                event_id: event?.id,
                price: draft.floatPrice,
                name: draft.name,
                quantity: Number(draft.quantity),
                type: draft.type || 'product',
                sale_type: SaleTypes.BATCH,
                absorbed_service_fee: absorbedServiceFee,
            };

            if (draft.description) {
                payload.description = draft.description;
            }

            if (draft.imageFile) {
                payload.image_id = await uploadProductImage(draft.imageFile);
            } else if (draft.image_id) {
                // Rascunho veio do catálogo já com foto própria — sem upload
                // novo, mantém a que já existe em vez de salvar sem imagem.
                payload.image_id = draft.image_id;
            }

            if (draft.product_template_id) {
                payload.product_template_id = draft.product_template_id;
            }

            payloads.push(payload);
        }

        return payloads;
    }

    // Cartão fechado esconde os campos: sem abrir o primeiro incompleto, o
    // produtor vê o botão travado e nenhuma explicação.
    function revealErrors() {
        setExistingProducts(prev => prev.map(p => ({ ...p, showErrors: true })));
        setNewProducts(prev => prev.map(p => ({ ...p, showErrors: true })));

        const firstInvalid = [...existingProducts, ...newProducts]
            .find(product => !isProductValid(product));

        if (firstInvalid) {
            setExpandedId(firstInvalid.internal_id);
            setPendingScrollId(firstInvalid.internal_id);
        }
    }

    async function updateProducts() {
        if (hasProductErrors) {
            revealErrors();
            return;
        }

        setLoading(true);

        try {
            const existingProductPayloads = await buildExistingProductPayloads();
            const newProductPayloads = await buildNewProductPayloads();
            const payload = {
                type: 'products',
                event_id: event?.id,
                products: [...existingProductPayloads, ...newProductPayloads]
            };

            const { data } = await ticketsApi.updateBulk(payload);
            await removeReplacedImages();
            onSuccess({
                ...event,
                ...data,
            })
        } catch (error) {
            // console.log('error')
        } finally {
            setLoading(false);
        }
    }

    async function createProducts() {
        if (hasProductErrors) {
            revealErrors();
            return;
        }

        setLoading(true);

        try {
            // Evento novo não tem produto pra atualizar: só o que foi criado aqui.
            const products = await buildNewProductPayloads();

            if (!products.length) {
                onSuccess(event);

                if (!isDraft) {
                    removeDraft();
                }
                return
            }

            const { data } = await ticketsApi.createBulk({ products });
            onSuccess({
                ...event,
                ...data,
            })

            if (!isDraft) {
                removeDraft();
            }
        } catch (error) {
            // console.log('error')
            setLoading(false);
        }
    }

    async function removeDraft() {
        try {
            await ticketsApi.removeDraft(event?.slug);
        } catch (error) {
            // console.log('error')
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container>
            <StepHeader>
                <StepTitle>Produtos do evento</StepTitle>
                <StepHint>
                    O que vai ser vendido além dos ingressos. Ajuste o que já está no
                    evento, crie novos produtos ou aceite uma sugestão.
                </StepHint>
                {totalChosen > 0 && (
                    <StepBadge>
                        <Check size={13} aria-hidden='true' />
                        {totalChosen} {totalChosen === 1 ? 'produto neste evento' : 'produtos neste evento'}
                    </StepBadge>
                )}
            </StepHeader>

            {
                loading ? (
                    <FullyCentered>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <Column>
                        {!!existingProducts.length && (
                            <StepSection
                                id='event-products'
                                icon={PackageCheck}
                                title='Já no evento'
                                hint='O que este evento vende hoje. Abra um produto para alterar nome, preço, estoque, descrição ou foto — as mudanças valem ao avançar.'
                                count={existingProducts.length}
                            >
                                {existingProducts.map(product => (
                                    <NewProductForm
                                        key={product.internal_id}
                                        domId={`product-draft-${product.internal_id}`}
                                        product={product}
                                        expanded={expandedId === product.internal_id}
                                        hasError={product.showErrors && !isProductValid(product)}
                                        onToggle={() => toggleExpanded(product.internal_id)}
                                        onChange={(patch) => updateExistingProduct(product.internal_id, patch)}
                                    />
                                ))}
                            </StepSection>
                        )}

                        <ProductCatalogPicker
                            products={availableProducts}
                            pagination={productsPagination}
                            loading={productsLoading}
                            onPaginate={onProductPaginate}
                            existingNames={takenProductNames}
                            pickedTemplateIds={pickedTemplateIds}
                            onAccept={acceptTemplate}
                        />

                        <ProductSuggestions
                            eventSlug={event?.slug}
                            existingNames={takenProductNames}
                            onAccept={acceptSuggestion}
                        />

                        <StepSection
                            id='new-products'
                            icon={PenLine}
                            title='Criar do zero'
                            hint='Não precisa já existir como modelo — cria e já atribui ao evento de uma vez.'
                            count={newProducts.length}
                        >
                            {newProducts.map(draft => (
                                <NewProductForm
                                    key={draft.internal_id}
                                    domId={`product-draft-${draft.internal_id}`}
                                    product={draft}
                                    expanded={expandedId === draft.internal_id}
                                    hasError={draft.showErrors && !isProductValid(draft)}
                                    onToggle={() => toggleExpanded(draft.internal_id)}
                                    onChange={(patch) => updateNewProduct(draft.internal_id, patch)}
                                    onRemove={() => removeNewProduct(draft.internal_id)}
                                />
                            ))}

                            <AddProductCard
                                type='button'
                                disabled={hasNewDraftErrors}
                                onClick={addNewProduct}
                            >
                                <AddProductIcon disabled={hasNewDraftErrors}>
                                    <Plus size={20} aria-hidden='true' />
                                </AddProductIcon>
                                <AddProductText>
                                    <AddProductTitle disabled={hasNewDraftErrors}>Novo produto</AddProductTitle>
                                    <AddProductHint>
                                        {hasNewDraftErrors
                                            ? 'Complete o produto acima antes de adicionar outro'
                                            : 'Nome, preço, quantidade e foto — leva um minuto'}
                                    </AddProductHint>
                                </AddProductText>
                            </AddProductCard>
                        </StepSection>
                    </Column>
                )}

            <BottomBar>
                {
                    loading ? (
                        <LoaderWrapper>
                            <Loader />
                        </LoaderWrapper>
                    ) : (
                        <BottomBarContent>
                            <BackButton onClick={() => goBackFormStep(2)}>
                                <ArrowLeft size={16} />
                                Voltar
                            </BackButton>
                            <SubmitButton
                                disabled={hasNewDraftErrors}
                                onClick={() => !hasNewDraftErrors && (!edit ? createProducts() : updateProducts())}
                            >
                                Avançar
                                <ArrowRight size={16} />
                            </SubmitButton>
                        </BottomBarContent>
                    )
                }
            </BottomBar>
        </Container >
    );
}

const Container = styled(Column)`
    overflow-x: auto;
    padding-bottom: 100px;
`

const StepHeader = styled.div`
    margin-bottom: 28px;
`;

const StepTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StepHint = styled.p`
    max-width: 620px;
    margin-top: 6px;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`;

const StepBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 6px 12px;
    border-radius: 20px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 12px;
    font-weight: 600;
`;

const BottomBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    border-top: 1px solid ${colors.night}15;
    box-shadow: 0 -4px 20px ${colors.night}0A;
    z-index: 10;
    padding: 14px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 75%;
    }
`;

const BottomBarContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    max-width: 500px;
    margin: 0 auto;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: 1.5px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '22' : panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;

    &:hover {
        background: ${({ disabled }) => disabled ? colors.night + '22' : panelAccent(colors.secondaryPurple)};
    }
`;

const AddProductCard = styled.button`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 18px 20px;
    text-align: left;
    background: transparent;
    border: 2px dashed ${({ disabled }) => disabled ? colors.night + '18' : panelAccentAlpha(colors.occasionalPurple, '33')};
    border-radius: 14px;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;

    ${({ disabled }) => !disabled && `
        &:hover {
            border-color: ${panelAccentAlpha(colors.occasionalPurple, '66')};
            background: ${panelAccentAlpha(colors.occasionalPurple, '05')};
        }
    `};

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const AddProductIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '10' : panelAccentAlpha(colors.occasionalPurple, '12')};
    color: ${({ disabled }) => disabled ? colors.night + '44' : panelAccent(colors.occasionalPurple)};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
`;

const AddProductText = styled.div``;

const AddProductTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${({ disabled }) => disabled ? colors.night + '55' : panelAccent(colors.occasionalPurple)};
    transition: color 0.2s ease;
`;

const AddProductHint = styled.div`
    font-size: 12px;
    color: ${colors.night}88;
    margin-top: 2px;
`;